import React, { useEffect, useRef } from "react";

const Waveform = ({ audioLevels = [], isRecording = false, style = {} }) => {
	const canvasRef = useRef(null);
	const prevFrameRef = useRef([]);

	// Set proper pixel ratio scaling once
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const dpr = window.devicePixelRatio || 1;

		// Actual drawing buffer = CSS size × DPR
		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width * dpr;
		canvas.height = rect.height * dpr;

		// Scale the drawing space
		const ctx = canvas.getContext("2d");
		ctx.scale(dpr, dpr);
	}, []);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const ctx = canvas.getContext("2d");

		// IMPORTANT:
		// Draw using CSS coordinate space (because we scaled the ctx)
		const rect = canvas.getBoundingClientRect();
		const { width } = rect;
		const { height } = rect;

		// Clear
		ctx.clearRect(0, 0, width, height);

		ctx.fillStyle = "rgba(0, 0, 0, 0.95)";
		ctx.fillRect(0, 0, width, height);

		// Grid
		ctx.strokeStyle = "rgba(75,85,99,0.22)";
		ctx.lineWidth = 1;
		for (let i = 0; i <= 4; i++) {
			const y = (height / 4) * i;
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(width, y);
			ctx.stroke();
		}
		for (let i = 0; i <= 8; i++) {
			const x = (width / 8) * i;
			ctx.beginPath();
			ctx.moveTo(x, 0);
			ctx.lineTo(x, height);
			ctx.stroke();
		}

		// ---- WAVEFORM PROCESSING ----

		const stableSize = 512;
		const input = audioLevels.length
			? audioLevels
			: new Array(stableSize).fill(128);

		// downsample to stable size
		const resampled = new Array(stableSize);
		const factor = input.length / stableSize;
		for (let i = 0; i < stableSize; i++) {
			resampled[i] = input[Math.floor(i * factor)] || 128;
		}

		// smoother — but balanced to avoid artificial midline
		const prev = prevFrameRef.current.length
			? prevFrameRef.current
			: resampled.slice();
		const smoothed = resampled.map((v, i) => prev[i] * 0.5 + v * 0.5);
		prevFrameRef.current = smoothed;

		// ---- DRAW WAVEFORM ----
		ctx.beginPath();
		ctx.lineWidth = 2.2; // thinner = less pixelation visibility
		ctx.strokeStyle = "#fbbf24";
		ctx.shadowBlur = 12;
		ctx.shadowColor = isRecording
			? "rgba(251,191,36,0.7)"
			: "rgba(251,191,36,0.35)";

		const mid = height / 2;
		const sliceWidth = width / stableSize;

		for (let i = 0; i < stableSize; i++) {
			const norm = (smoothed[i] - 128) / 128; // -1..1
			const y = mid + norm * (height * 0.35); // amplitude

			const x = i * sliceWidth;

			if (i === 0) {
				ctx.moveTo(x, y);
			} else {
				ctx.lineTo(x, y);
			}
		}

		ctx.stroke();
	}, [audioLevels, isRecording]);

	return (
		<div
			style={{
				padding: "16px",
				backgroundColor: "rgba(0,0,0,0.9)",
				borderRadius: "16px",
				border: "2px solid rgba(234, 179, 8, 0.3)",
				...style,
			}}
		>
			<canvas
				ref={canvasRef}
				style={{
					width: "100%",
					height: "200px",
					display: "block",
				}}
			/>
		</div>
	);
};

export default Waveform;
